import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { getImageUrl, joinPublicStorefrontUrl } from "../api/client";
import { useDispatch } from "react-redux";
import { addToCart } from "../Features/Cart/cartSlice";
import Seo from "../Components/Seo/Seo";
import { useSiteSeo } from "../context/SiteSeoContext";
import { usePageSeo } from "../hooks/usePageSeo";
import { useManagedPageContent } from "../hooks/useManagedPageContent";
import BlockRenderer from "../Components/BlockRenderer/BlockRenderer";
import { usePrerenderReady } from "../utils/prerenderReady";
import ContextHeroSlider from "../Components/Hero/ContextHeroSlider";
import toast from "react-hot-toast";
import { FaCartPlus, FaGraduationCap, FaBriefcase, FaGlassCheers, FaTrophy } from "react-icons/fa";
import { MdTheaterComedy } from "react-icons/md";
import { buildOccasionFacets, filterOccasionProducts } from "./occasionFilters";
import "./Occasions.css";

function ProductCard({ product }) {
  const dispatch = useDispatch();
  const imageUrl = getImageUrl(
    product.first_image ||
      (Array.isArray(product.images) && product.images[0]?.url) ||
      (Array.isArray(product.images) && product.images[0]) ||
      null
  );
  const price = product.min_price_paise != null
    ? `₹${(product.min_price_paise / 100).toLocaleString("en-IN")}`
    : null;

  const handleAddToCart = (e) => {
    e.preventDefault();
    dispatch(addToCart({
      productId: product.id,
      productName: product.name,
      productPrice: product.min_price_paise ? product.min_price_paise / 100 : 0,
      productImage: imageUrl,
      quantity: 1,
    }));
    toast.success(`${product.name} added to cart!`);
  };

  return (
    <Link to={`/product/${product.id}`} className="occ-card">
      <div className="occ-card-img-wrap">
        {imageUrl ? (
          <img src={imageUrl} alt={product.name} className="occ-card-img" loading="lazy" />
        ) : (
          <div className="occ-card-img-placeholder" />
        )}
        <button
          className="occ-card-cart-btn"
          onClick={handleAddToCart}
          aria-label={`Add ${product.name} to cart`}
        >
          <FaCartPlus />
        </button>
      </div>
      <div className="occ-card-info">
        <p className="occ-card-name">{product.name}</p>
        {price && <p className="occ-card-price">From {price}</p>}
      </div>
    </Link>
  );
}

export default function Occasions() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ category: "", color: "", size: "", maxPrice: null });
  const [categorySearch, setCategorySearch] = useState("");
  const siteCtx = useSiteSeo();
  const orgName = siteCtx.organization_name?.trim() || "Gem Fashion Studios";
  const { seo_title: pageSeoTitle, seo_description: pageSeoDesc, og_image_url: pageOgImage } = usePageSeo('occasions');
  const { blocks, loaded: contentLoaded } = useManagedPageContent('occasions');

  usePrerenderReady(!loading && contentLoaded);

  useEffect(() => {
    setLoading(true);
    fetch(joinPublicStorefrontUrl("/api/products?category=Occasion"))
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const facets = useMemo(() => buildOccasionFacets(products), [products]);
  const visibleCategories = useMemo(() => {
    const query = categorySearch.trim().toLowerCase();
    return query ? facets.categories.filter(({ name }) => name.toLowerCase().includes(query)) : facets.categories;
  }, [facets.categories, categorySearch]);

  const displayed = useMemo(() => filterOccasionProducts(products, filters), [products, filters]);
  const setFilter = (name, value) => setFilters((current) => ({ ...current, [name]: value }));

  return (
    <>
      <Seo
        title={pageSeoTitle || `Occasions — Elegant Event Outfits | ${orgName}`}
        description={pageSeoDesc || "Dressed for every milestone. Discover refined ethnic and contemporary outfits for corporate events, galas, ceremonies, and more — available to rent."}
        canonicalPath="/occasions"
        ogImage={pageOgImage || undefined}
      />

      {/* Hero slider — admin-managed via Hero Sliders → Shop → "Occasion" */}
      <ContextHeroSlider context="shop" contextKey="Occasion" />

      {blocks ? <BlockRenderer content={blocks} /> : <section className="occ-categories">
        <div className="occ-cat-item">
          <FaGraduationCap className="occ-cat-icon" />
          <span>Graduations</span>
        </div>
        <div className="occ-cat-item">
          <FaBriefcase className="occ-cat-icon" />
          <span>Corporate Events</span>
        </div>
        <div className="occ-cat-item">
          <MdTheaterComedy className="occ-cat-icon" />
          <span>Cultural Programmes</span>
        </div>
        <div className="occ-cat-item">
          <FaGlassCheers className="occ-cat-icon" />
          <span>Receptions</span>
        </div>
        <div className="occ-cat-item">
          <FaTrophy className="occ-cat-icon" />
          <span>Award Ceremonies</span>
        </div>
      </section>}

      {/* Product grid */}
      <section className="occ-grid-section">
        {loading ? (
          <div className="occ-skeleton-grid">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="occ-skeleton-card" />
            ))}
          </div>
        ) : (
          <div className="occ-catalog-layout">
            <aside className="occ-filter" aria-label="Filter occasion outfits">
              <div className="occ-filter-topline">
                <h2>Product filters</h2>
                {Object.values(filters).some((value) => value !== "" && value != null) && (
                  <button type="button" onClick={() => setFilters({ category: "", color: "", size: "", maxPrice: null })}>Clear</button>
                )}
              </div>
              <details open>
                <summary>Product categories</summary>
                <input type="search" value={categorySearch} onChange={(event) => setCategorySearch(event.target.value)} placeholder="Search categories" aria-label="Search categories" />
                <div className="occ-filter-options">
                  {visibleCategories.map(({ name, count }) => (
                    <button type="button" key={name} className={filters.category === name ? "active" : ""} onClick={() => setFilter("category", filters.category === name ? "" : name)}>
                      <span>{name}</span><small>({count})</small>
                    </button>
                  ))}
                </div>
              </details>
              <details>
                <summary>Color</summary>
                <div className="occ-filter-options">
                  {facets.colors.map(({ name, count }) => (
                    <button type="button" key={name} className={filters.color === name ? "active" : ""} onClick={() => setFilter("color", filters.color === name ? "" : name)}>
                      <span>{name}</span><small>({count})</small>
                    </button>
                  ))}
                </div>
              </details>
              <details>
                <summary>Sizes</summary>
                <div className="occ-filter-size-options">
                  {facets.sizes.map(({ name }) => (
                    <button type="button" key={name} className={filters.size === name ? "active" : ""} onClick={() => setFilter("size", filters.size === name ? "" : name)}>{name}</button>
                  ))}
                </div>
              </details>
              <details>
                <summary>Price (from / day)</summary>
                <input type="range" min={facets.minPrice} max={facets.maxPrice || 1} value={filters.maxPrice ?? facets.maxPrice} onChange={(event) => setFilter("maxPrice", Number(event.target.value))} aria-label="Maximum daily rental price" />
                <p className="occ-filter-price">Up to ₹{Math.round(filters.maxPrice ?? facets.maxPrice).toLocaleString("en-IN")}</p>
              </details>
            </aside>
            <div className="occ-results">
              <p className="occ-results-count">{displayed.length} outfit{displayed.length === 1 ? "" : "s"}</p>
              {displayed.length === 0 ? (
                <div className="occ-empty"><p>No outfits match these filters. <button type="button" onClick={() => setFilters({ category: "", color: "", size: "", maxPrice: null })}>Clear filters</button></p></div>
              ) : (
                <div className="occ-grid">
                  {displayed.map((p) => <ProductCard key={p.id} product={p} />)}
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {!blocks && <section className="occ-bottom-cta">
        <h2>Need styling advice?</h2>
        <p>Our team is happy to help you pick the perfect outfit for your event — just reach out.</p>
        <div className="occ-bottom-cta-btns">
          <Link to="/shop" className="occ-cta-btn primary">Browse all styles</Link>
          <Link to="/contact" className="occ-cta-btn secondary">Talk to us</Link>
        </div>
      </section>}
    </>
  );
}
